/**
 * Platform detection utilities.
 *
 * Provides a single source of truth for platform guards across the codebase.
 * On web/desktop, mobile-only features gracefully degrade by checking these
 * flags before calling into native modules.
 */

import { Platform } from 'react-native';

// ── Type augmentation for the Electron preload bridge ────────────────────────

/** A folder the user has granted Ellie access to, bound to one plan. */
export interface FolderGrant {
  /** Absolute path on disk. */
  path: string;
  /** Folder basename, for display. */
  name: string;
  /** ms epoch the grant was created. */
  grantedAt: number;
}

/** One file surfaced by a folder scan — metadata only, no contents. */
export interface ScannedFile {
  /** Absolute path on disk. */
  path: string;
  name: string;
  /** Lowercased extension without the dot (e.g. "pdf"). */
  ext: string;
  /** Size in bytes. */
  size: number;
  /** Last-modified time, ms epoch. */
  mtime: number;
  /** Basename of the granted root this file was found under. */
  folder: string;
}

/**
 * One file a plan has already pulled off disk, remembered by the Electron main
 * process so a re-scan of the same folder doesn't re-offer it. Identified by
 * path + size + mtime, so an edited file surfaces again but an untouched one
 * stays hidden. See desktop/main.ts "folder-scan already pulled ledger".
 */
export interface PulledFile {
  /** Normalized absolute path as of the pull. */
  path: string;
  name: string;
  size: number;
  /** Last-modified ms epoch, rounded to whole ms. */
  mtime: number;
  /** ms epoch this file was uploaded into the plan. */
  pulledAt: number;
  /** Document id from /documents/create, when it was known. */
  documentId?: string | null;
}

export interface ElectronBridge {
  isElectron: true;
  platform: 'darwin' | 'win32' | 'linux';
  /**
   * Filesystem access for the desktop "let Ellie look through my files"
   * feature. The renderer is sandboxed, so every call is mediated by the main
   * process. Grants are namespaced by plan id — a folder picked from one plan
   * is bound to that plan, and reads are scope-checked against it in main, so
   * one plan can never read another plan's folder.
   */
  files: {
    /** Open the native folder panel and bind the chosen folder(s) to a plan. */
    grant:  (projectId: string | number) => Promise<FolderGrant[]>;
    /** Folders currently connected to a plan. */
    grants: (projectId: string | number) => Promise<FolderGrant[]>;
    /** Walk a plan's granted folders → file metadata only. */
    scan:   (projectId: string | number) => Promise<ScannedFile[]>;
    /** Read one file as base64 — rejects if outside the plan's granted roots. */
    read:   (projectId: string | number, filePath: string) => Promise<string>;
    /** Revoke one folder grant, or all of a plan's grants when path omitted. */
    revoke: (projectId: string | number, folderPath?: string) => Promise<FolderGrant[]>;
    /** Files this plan has already pulled off disk, so a re-scan can skip them. */
    pulled: (projectId: string | number) => Promise<PulledFile[]>;
    /** Record files as pulled into a plan; returns the plan's updated ledger. */
    recordPull: (
      projectId: string | number,
      entries: { path: string; name?: string; size: number; mtime: number; documentId?: string | number | null }[],
    ) => Promise<PulledFile[]>;
  };
  secureStore: {
    get:    (key: string) => Promise<string | null>;
    set:    (key: string, value: string) => Promise<void>;
    delete: (key: string) => Promise<void>;
  };
  notification: {
    show: (title: string, body: string, data?: unknown) => Promise<void>;
  };
  openExternal:      (url: string) => Promise<void>;
  getAppVersion:     () => Promise<string>;
  getPlatform:       () => Promise<string>;
  getSystemTheme:    () => Promise<'light' | 'dark'>;
  installUpdate:     () => void;
  checkForUpdates:   () => Promise<{ ok: boolean; devMode?: boolean; error?: string }>;
  openMainWindow:    (screenName?: string) => void;
  closeMenuCard:     () => void;
  setQuickActionsContext: (payload: {
    authorized: boolean;
    firstName?: string;
    pendingActions?: number;
    activeReminders?: number;
    themeMode?: 'light' | 'dark';
  }) => void;
  getQuickActionsContext: () => Promise<{
    authorized: boolean;
    firstName?: string;
    pendingActions?: number;
    activeReminders?: number;
    themeMode?: 'light' | 'dark';
  }>;
  /** Lock the app immediately (shows lock screen overlay). */
  lockScreen:        () => void;
  /** Notify main that the user successfully authenticated — clears the lock. */
  unlockScreen:      () => void;
  /** Set idle-inactivity timeout (minutes) before auto-lock. */
  setIdleTimeout:    (minutes: number) => void;
  /** Prompt Touch ID / Windows Hello. */
  promptBiometrics:  (reason: string) => Promise<{ success: boolean; error?: string }>;
  /** Returns true if biometric auth is available on this device. */
  biometricsAvailable: () => Promise<boolean>;
  /** Write to clipboard and auto-clear after ms. */
  clipboardWriteSecure: (text: string, clearAfterMs?: number) => void;
  /**
   * Push the user's "Allow Screenshots" preference to the main process so it
   * can flip `setContentProtection` on the main window and persist the value
   * for next launch. Renderer is the source of truth (AsyncStorage); main keeps
   * a mirror so it can apply the right value BEFORE the renderer loads.
   */
  setAllowScreenshots: (allow: boolean) => Promise<void>;
  onDeepLink:        (cb: (url: string) => void) => () => void;
  onUpdateAvailable: (cb: () => void) => () => void;
  onNotificationClicked: (cb: (data: unknown) => void) => () => void;
  onSystemThemeChanged:  (cb: (theme: 'light' | 'dark') => void) => () => void;
  /** Fired when the app should show the lock screen. */
  onLockScreen:      (cb: (payload: { reason: string }) => void) => () => void;
  /** Fired when a native menu item triggers in-app navigation. */
  onNavigateTo:      (cb: (payload: { screen: string }) => void) => () => void;
}

declare global {
  interface Window {
    electronBridge?: ElectronBridge;
    __hideSplash?: () => void;
  }
}

// ── Derived flags ─────────────────────────────────────────────────────────────

/** True when running inside Electron (desktop app). */
export const isElectron: boolean =
  typeof window !== 'undefined' && !!window.electronBridge;

/** True on any web context (browser or Electron). */
export const isWeb: boolean = Platform.OS === 'web';

/** True when running on a desktop device (Electron or web browser). */
export const isDesktop: boolean = isElectron || isWeb;

/** True when running on a native mobile device. */
export const isMobile: boolean = Platform.OS === 'ios' || Platform.OS === 'android';

export const isIOS: boolean     = Platform.OS === 'ios';
export const isAndroid: boolean = Platform.OS === 'android';

// ── Electron bridge accessor ──────────────────────────────────────────────────

/** Returns the Electron IPC bridge, or null when not in Electron. */
export function getElectronBridge(): ElectronBridge | null {
  return (typeof window !== 'undefined' && window.electronBridge) ? window.electronBridge : null;
}

// ── Screen breakpoints ────────────────────────────────────────────────────────

/** Minimum window width that triggers the desktop sidebar layout. */
export const DESKTOP_BREAKPOINT  = 1024;

/**
 * Outer padding around the floating desktop widget frame (web + Electron).
 * The single source of truth — DesktopLayout paints the frame with it, and
 * every desktop-positioned overlay (Notification toast, update banners)
 * anchors just inside the frame using the same value. Was 64; shrunk to
 * reclaim viewport. If Electron ever needs its own value, branch on
 * isElectron here so all anchors move together.
 */
export const WIDGET_GUTTER = 28;

/** Minimum window width for tablet-style layout (wider content, less padding reduction). */
export const TABLET_BREAKPOINT   = 768;

/**
 * Returns the layout mode for a given window width.
 * - 'mobile':  < 768 — identical to the native mobile app
 * - 'tablet':  768–1023 — slightly wider, same bottom nav
 * - 'desktop': ≥ 1024 — sidebar navigation, two-column layouts
 */
export function getLayoutMode(width: number): 'mobile' | 'tablet' | 'desktop' {
  if (width >= DESKTOP_BREAKPOINT) return 'desktop';
  if (width >= TABLET_BREAKPOINT)  return 'tablet';
  return 'mobile';
}
